import {isEmpty} from '../../../utils/common'
import {formatDate} from '../../../utils/date'

export const defaultProjectTicketValues = {
  currentColumnId: 0,
  moduleId: 0,
  status: 0,
  priority: 0,
  title: '',
  description: null,
  assignedTo: '',
  spentTime: '',
  estimation: '',
  dueDate: null,
  startDate: null,
}

export const setDefaultTicketDetails = (ticketDetails: any, reset: any) => {
  if (isEmpty(ticketDetails)) return
  return reset({
    assignedTo: ticketDetails?.assignee,
    currentColumnId: ticketDetails?.columnID,
    moduleId: ticketDetails?.moduleID,
    status: ticketDetails?.status,
    priority: ticketDetails?.priority,
    title: ticketDetails?.title,
    description: ticketDetails?.description,
    spentTime: ticketDetails?.spent_time,
    estimation: ticketDetails?.estimation,
    dueDate: ticketDetails?.due_date,
    startDate: ticketDetails?.start_date,
  })
}

export const defaultCustomFieldsValues = (customFormFields: any) => {
  return customFormFields.reduce((acc: any, field: any) => {
    if (!field.isLabelOnly) {
      acc[field.fieldId] = {
        [field.valueType]: field.defaultValues,
      }
    }
    return acc
  }, {})
}

export const dataToFormFields = (customFields: any) => {
  if (isEmpty(customFields)) return []

  return [
    {
      id: 'line-break',
      isLabelOnly: true,
      label: 'Custom Fields',
      labelClass: 'form-label w-100 fw-bolder fs-3 mb-8 pb-2 border-bottom mt-8',
    },
    ...customFields.map((customField: any) => {
      const field: any = {
        id: `custom-field-${customField.id}`,
        fieldId: customField?.id,
        label: customField.name,
        readOnly: customField.read_only,
      }

      if (customField.datatype === 'text') {
        field.registerKey = `${customField.id}[strValue]`
        field.valueType = 'strValue'
        field.defaultValues = customField.str_value || ''
        field.maxLength = 255
      }

      if (customField.datatype === 'select') {
        field.isSelect = true
        field.registerKey = `${customField.id}[selectValue]`
        field.valueType = 'selectValue'
        field.defaultValues = customField?.str_value || ''
        field.placeholder = 'Select an option'
        field.isNullable = true
        field.options = customField?.group_values
          ?.split(',')
          .map((val: any) => ({label: val.trim(), value: val.trim()}))
      }

      if (customField.datatype === 'multi select') {
        field.isMultiSelect = true
        field.registerKey = `${customField.id}[multiSelectValue]`
        field.valueType = 'multiSelectValue'
        field.defaultValues = customField?.str_value?.split(', ') || []
        field.inputClass = 'w-100'
        field.placeholder = 'Select an option'
        field.options = customField?.group_values
          ?.split(',')
          .map((val: any) => ({label: val.trim(), value: val.trim()}))
      }

      if (customField.datatype === 'number') {
        field.isNumberInput = true
        field.isFloat = true
        field.registerKey = `${customField.id}[numberValue]`
        field.valueType = 'numberValue'
        field.defaultValues = customField.number_value || null
        field.maxLength = 255
      }

      if (customField.datatype === 'date') {
        field.isInputDate = true
        field.registerKey = `${customField.id}[dateValue]`
        field.valueType = 'dateValue'
        field.defaultValues = customField.str_value || null
        field.isNullable = true
      }
      return field
    }),
  ]
}

export const ticketTitleTextareaHeight = (textareaRef: any) => {
  if (textareaRef.current) {
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
  }
}

export const groupedHistoryByUser = (ticketActivitiesHistory: any[]) => {
  return ticketActivitiesHistory.reduce((acc: any[], activity: any, index: number) => {
    const prevActivity: any = ticketActivitiesHistory[index - 1]
    const isNewGroup = !prevActivity || prevActivity.created_by !== activity.created_by

    if (isNewGroup) {
      acc.push([activity])
    } else {
      acc[acc.length - 1].push(activity)
    }
    return acc
  }, [] as any[][])
}

export const formattedActivities = (activity: any) => {
  const formatComment = (input: string) =>
    input.replace(/<p>(.*?)#.*?<\/p>/, (_, beforeHash) => `@${beforeHash.trim()}`)
  switch (activity.field_name) {
    case 'description':
      return 'Description changed.'

    case 'attachment':
      const oldDocument = formatComment(activity.old_value)
      const newDocument = formatComment(activity.new_value)

      return activity.old_value === '' && activity.new_value !== ''
        ? `Attachment uploaded: "${newDocument}"`
        : activity.new_value === '' && activity.old_value !== ''
        ? `Attachment deleted: "${oldDocument}"`
        : '-'

    case 'comment':
      const oldComment = formatComment(activity.old_value)
      const newComment = formatComment(activity.new_value)

      return activity.old_value === '' && activity.new_value !== ''
        ? `Comment added: "${newComment}"`
        : activity.new_value === '' && activity.old_value !== ''
        ? `Comment deleted: "${oldComment}"`
        : `Comment edited: "${oldComment}" → "${newComment}"`

    case 'due_date':
      return `Due date: "${
        activity.old_value ? formatDate(activity.old_value, true) : 'None'
      }" → "${activity.new_value ? formatDate(activity.new_value, true) : 'None'}"`

    case 'start_date':
      return `Start date: "${
        activity.old_value ? formatDate(activity.old_value, true) : 'None'
      }" → "${activity.new_value ? formatDate(activity.new_value, true) : 'None'}"`

    default:
      return `${activity.field_lable}: ${activity.old_value || 'None'} → ${
        activity.new_value || 'None'
      }`
  }
}

export const isTicketDescriptionEmpty = (description: string) => {
  if (description === '<p class="textEditor_paragraph"><br></p>') {
    return true
  } else {
    return false
  }
}

export const defaultSpentTimeModalValues = (logData: any, isEdit: boolean) => {
  const defaultTodayDate = (): string => {
    const now = new Date()
    const year = now.getUTCFullYear()
    const month = String(now.getUTCMonth() + 1).padStart(2, '0')
    const day = String(now.getUTCDate()).padStart(2, '0')
    const hours = String(now.getUTCHours()).padStart(2, '0')
    const minutes = String(now.getUTCMinutes()).padStart(2, '0')
    const seconds = String(now.getUTCSeconds()).padStart(2, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  }

  return {
    spent_time_created_at: isEdit ? logData.spent_time_created_at : defaultTodayDate(),
    ...(isEdit
      ? {spent_time_diff: logData?.spent_time_diff || ''}
      : {spent_time: logData?.spent_time || ''}),
    log_description: logData?.log_description || '',
  }
}

export const ticketActionMenuActions = (
  eventKey: string,
  setMenuEvent: any,
  setIsTicketEditable: any,
  setShowCreateTicketModal: any,
  setShowDeleteModal: any
) => {
  setMenuEvent(eventKey)
  switch (eventKey) {
    case 'edit':
      setIsTicketEditable(true)
      break
    case 'parent-ticket':
      setShowCreateTicketModal(true)
      break
    case 'sub-ticket':
      setShowCreateTicketModal(true)
      break
    case 'delete':
      setShowDeleteModal(true)
      break
  }
}

// Default Values
export const createTFromTDefaultValues = (
  columns: any,
  modules: any,
  statuses: any,
  priorities: any,
  ticketDetails: any
) => {
  const {columnID, moduleID} = ticketDetails
  const defaultStatus = statuses?.find((status: any) => status?.is_default)
    ? statuses?.find((status: any) => status?.is_default)?.id
    : statuses?.[0]?.id || 1

  const defaultPriority = priorities?.find((priority: any) => priority?.is_default)
    ? priorities.find((priority: any) => priority?.is_default)?.id
    : priorities?.[0]?.id || 1

  return {
    ticketSource: 'existing',
    moduleId: moduleID || modules?.[0]?.module_id,
    currentColumnId: columnID || columns?.[0]?.column_id,
    status: defaultStatus,
    priority: defaultPriority,
    title: '',
    description: '<p class="textEditor_paragraph"><br></p>',
    assignedTo: '',
    estimation: '',
    dueDate: null,
    isArchived: false,
    parentCardId: null,
    childCardId: null,
  }
}

export const onTicketSourceChange = (resourceType: string, reset: any, defaultValues: any) => {
  if (resourceType === 'new') {
    reset({...defaultValues, ticketSource: 'new'})
  } else {
    reset({...defaultValues, ticketSource: 'existing'})
  }
}

export const setLinkedTicketId = (
  isSourceNew: boolean,
  menuEvent: any,
  ticketDetails: any,
  setValue: any
) => {
  if (isSourceNew) {
    setValue(
      menuEvent === 'parent-ticket' ? 'childCardId' : 'parentCardId',
      ticketDetails?.tickedID,
      {shouldDirty: true}
    )
  }
}

export const filterLinkedTickets = (projectTickets: any, ticketDetails: any, menuEvent: any) =>
  projectTickets?.filter((ticket: any) => {
    if (!ticket?.tickedID || ticket.tickedID === ticketDetails?.tickedID) return false
    
    const linkedCards =
      menuEvent === 'parent-ticket' ? ticketDetails?.parent_cards : ticketDetails?.child_cards

    // If menuEvent is "parent-ticket", exclude child_cards tickets
    const isInChildCards =
      menuEvent === 'parent-ticket' &&
      ticketDetails?.child_cards?.some(
        (childTicket: any) => childTicket?.tickedID === ticket.tickedID
      )

    // If menuEvent is "child-ticket", exclude parent_cards tickets
    const isInParentCards =
      (menuEvent === 'child-ticket' || menuEvent === 'sub-ticket') &&
      ticketDetails?.parent_cards?.some(
        (parentTicket: any) => parentTicket?.tickedID === ticket.tickedID
      )

    return (
      !linkedCards?.length ||
      (!linkedCards.some((linkedTicket: any) => linkedTicket?.tickedID === ticket.tickedID) &&
        !isInParentCards &&
        !isInChildCards)
    )
  })



  export function getFileIconClass(extension: string = ''): string {
    // Map extension -> icon class
    const iconMap: Record<string, string> = {
      pdf: 'bi bi-filetype-pdf text-danger',
      ppt: 'bi bi-filetype-ppt text-warning',
      pptx: 'bi bi-filetype-ppt text-warning',
      doc: 'bi bi-filetype-doc text-primary',
      docx: 'bi bi-filetype-doc text-primary',
      xls: 'bi bi-filetype-xls text-success',
      xlsx: 'bi bi-filetype-xls text-success',
      csv: 'bi bi-filetype-csv text-success',
      mp4: 'bi bi-file-earmark-play text-info',
      avi: 'bi bi-file-earmark-play text-info',
      mov: 'bi bi-file-earmark-play text-info',
      mpeg: 'bi bi-file-earmark-play text-info',
      mp3: 'bi bi-file-earmark-music text-info',
      wav: 'bi bi-file-earmark-music text-info',
      jpg: 'bi bi-file-earmark-image text-success',
      jpeg: 'bi bi-file-earmark-image text-success',
      png: 'bi bi-file-earmark-image text-success',
      gif: 'bi bi-file-earmark-image text-success',
      zip: 'bi bi-file-earmark-zip',
      rar: 'bi bi-file-earmark-zip',
      svg: 'bi bi-filetype-svg text-primary',
      txt: 'bi bi-filetype-txt',
    }
  
    // Convert the extension to lowercase and look it up
    const ext = extension.toLowerCase()
    // If not found in the map, use a generic file icon
    return iconMap[ext] || 'la la-file la-2x'
  }

  export function formatFileSize(bytes: number, decimals = 1): string {
    if (bytes === 0) return '0 Bytes'
  
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(dm))
  
    return `${size} ${sizes[i]}`
  }
  

  export function parseAttachments(data: any) {
    return data
      .filter((item: any) => item?.file_name && item?.file_size != null && item?.file_type)
      .map((item: any) => ({
        ...item,
        name: item.relative_path?.split('/').pop(),
        size: item.file_size,
        type: item.file_type,
      }))
  }
  